import { c as _c } from "react/compiler-runtime";
import { COUNTRIES, diToFormData, IDENTITY_ADDRESS_ANCHOR_DI, US_STATES_AND_ARMED_FORCES, VAULT_DOT_SEPARATOR, type VaultFormData } from '@onefootprint/core';
import type { CustomRequirementContentPrompt, Iso3166TwoDigitCountryCode, Language, Locale } from '@onefootprint/request-types';
import { Form, MarkdownText } from '@onefootprint/ui';
import type { Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Dropdown } from './dropdown';
import { NumberInput } from './number-input';
import { ShortTextInput } from './short-text-input';
type CountryOption = {
  value: Iso3166TwoDigitCountryCode;
  label: string;
};
const filterCountryOptions = (supportedCountries: Iso3166TwoDigitCountryCode[] | null | undefined): CountryOption[] | undefined => {
  if (!supportedCountries || supportedCountries.length === 0) return undefined;
  const allowed = new Set(supportedCountries);
  return COUNTRIES.filter(c => allowed.has(c.value as Iso3166TwoDigitCountryCode)) as CountryOption[];
};
type PromptRendererProps = {
  content: CustomRequirementContentPrompt;
  l10n?: {
    locale?: Locale;
    language?: Language;
  };
  disableSsnValidation?: boolean;
};
export const PromptRenderer = t0 => {
  const $ = _c(150);
  const {
    content: t1,
    l10n,
    disableSsnValidation
  } = t0;
  const {
    config,
    di,
    prompt,
    required
  } = t1;
  let t2;
  if ($[0] !== di) {
    t2 = diToFormData(di);
    $[0] = di;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const name = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      keyPrefix: "collectCustomData.pages.customDataForm"
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const {
    t
  } = useTranslation("idv2", t3);
  let t4;
  if ($[3] !== required || $[4] !== t) {
    t4 = required ? t(_temp) : undefined;
    $[3] = required;
    $[4] = t;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t4) {
    t5 = {
      required: t4
    };
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const requiredRules = t5;
  let t6;
  if ($[8] !== config.kind || $[9] !== prompt || $[10] !== required || $[11] !== t) {
    t6 = required || config.kind === "checkbox" ? prompt : `${prompt} (${t(_temp2)})`;
    $[8] = config.kind;
    $[9] = prompt;
    $[10] = required;
    $[11] = t;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const displayLabel = t6;
  if (config.kind === "checkboxes") {
    let t7;
    if ($[13] !== displayLabel) {
      t7 = <div className="text-label-2 text-primary">{displayLabel}</div>;
      $[13] = displayLabel;
      $[14] = t7;
    } else {
      t7 = $[14];
    }
    const t8 = name as Path<VaultFormData>;
    let t9;
    if ($[15] !== config.options) {
      t9 = config.options.map(_temp3);
      $[15] = config.options;
      $[16] = t9;
    } else {
      t9 = $[16];
    }
    let t10;
    if ($[17] !== requiredRules || $[18] !== t8 || $[19] !== t9) {
      t10 = <Form.CheckboxGroup name={t8} options={t9} rules={requiredRules} className="flex flex-col gap-3" />;
      $[17] = requiredRules;
      $[18] = t8;
      $[19] = t9;
      $[20] = t10;
    } else {
      t10 = $[20];
    }
    let t11;
    if ($[21] !== t10 || $[22] !== t7) {
      t11 = <div className="flex flex-col gap-2">{t7}{t10}</div>;
      $[21] = t10;
      $[22] = t7;
      $[23] = t11;
    } else {
      t11 = $[23];
    }
    return t11;
  }
  if (config.kind === "short_text") {
    const t7 = l10n?.locale;
    let t8;
    if ($[24] !== config || $[25] !== disableSsnValidation || $[26] !== displayLabel || $[27] !== name || $[28] !== requiredRules || $[29] !== t7) {
      t8 = <ShortTextInput name={name} label={displayLabel} requiredRules={requiredRules} config={config} locale={t7} disableSsnValidation={disableSsnValidation} />;
      $[24] = config;
      $[25] = disableSsnValidation;
      $[26] = displayLabel;
      $[27] = name;
      $[28] = requiredRules;
      $[29] = t7;
      $[30] = t8;
    } else {
      t8 = $[30];
    }
    return t8;
  }
  if (config.kind === "long_text") {
    const t7 = name as Path<VaultFormData>;
    let t8;
    if ($[31] !== config.placeholder || $[32] !== displayLabel || $[33] !== requiredRules || $[34] !== t7) {
      t8 = <Form.Textarea name={t7} label={displayLabel} rules={requiredRules} placeholder={config.placeholder} />;
      $[31] = config.placeholder;
      $[32] = displayLabel;
      $[33] = requiredRules;
      $[34] = t7;
      $[35] = t8;
    } else {
      t8 = $[35];
    }
    return t8;
  }
  if (config.kind === "number") {
    const t7 = l10n?.locale;
    let t8;
    if ($[36] !== config.format || $[37] !== config.max || $[38] !== config.min || $[39] !== config.placeholder || $[40] !== displayLabel || $[41] !== name || $[42] !== requiredRules || $[43] !== t7) {
      t8 = <NumberInput name={name} label={displayLabel} requiredRules={requiredRules} format={config.format} min={config.min} max={config.max} placeholder={config.placeholder} locale={t7} />;
      $[36] = config.format;
      $[37] = config.max;
      $[38] = config.min;
      $[39] = config.placeholder;
      $[40] = displayLabel;
      $[41] = name;
      $[42] = requiredRules;
      $[43] = t7;
      $[44] = t8;
    } else {
      t8 = $[44];
    }
    return t8;
  }
  if (config.kind === "checkbox") {
    const t7 = name as Path<VaultFormData>;
    let t8;
    if ($[45] !== displayLabel) {
      t8 = <MarkdownText className="text-body-2 text-primary">{displayLabel}</MarkdownText>;
      $[45] = displayLabel;
      $[46] = t8;
    } else {
      t8 = $[46];
    }
    let t9;
    if ($[47] !== requiredRules || $[48] !== t7 || $[49] !== t8) {
      t9 = <Form.Checkbox name={t7} label={t8} rules={requiredRules} />;
      $[47] = requiredRules;
      $[48] = t7;
      $[49] = t8;
      $[50] = t9;
    } else {
      t9 = $[50];
    }
    return t9;
  }
  if (config.kind === "dropdown") {
    let t7;
    if ($[51] !== config.includeOther || $[52] !== config.options || $[53] !== config.placeholder || $[54] !== displayLabel || $[55] !== name || $[56] !== required) {
      t7 = <Dropdown name={name} label={displayLabel} options={config.options} includeOther={config.includeOther} placeholder={config.placeholder} required={required} />;
      $[51] = config.includeOther;
      $[52] = config.options;
      $[53] = config.placeholder;
      $[54] = displayLabel;
      $[55] = name;
      $[56] = required;
      $[57] = t7;
    } else {
      t7 = $[57];
    }
    return t7;
  }
  if (config.kind === "multiple_choice") {
    let t7;
    if ($[58] !== displayLabel) {
      t7 = <div className="text-label-2 text-primary">{displayLabel}</div>;
      $[58] = displayLabel;
      $[59] = t7;
    } else {
      t7 = $[59];
    }
    const t8 = name as Path<VaultFormData>;
    let t9;
    if ($[60] !== config.options) {
      t9 = config.options.map(_temp4);
      $[60] = config.options;
      $[61] = t9;
    } else {
      t9 = $[61];
    }
    let t10;
    if ($[62] !== requiredRules || $[63] !== t8 || $[64] !== t9) {
      t10 = <Form.RadioGroup name={t8} options={t9} rules={requiredRules} />;
      $[62] = requiredRules;
      $[63] = t8;
      $[64] = t9;
      $[65] = t10;
    } else {
      t10 = $[65];
    }
    let t11;
    if ($[66] !== t10 || $[67] !== t7) {
      t11 = <div className="flex flex-col gap-2">{t7}{t10}</div>;
      $[66] = t10;
      $[67] = t7;
      $[68] = t11;
    } else {
      t11 = $[68];
    }
    return t11;
  }
  if (config.kind === "address") {
    let t7;
    if ($[69] !== config.supportedCountries) {
      t7 = filterCountryOptions(config.supportedCountries);
      $[69] = config.supportedCountries;
      $[70] = t7;
    } else {
      t7 = $[70];
    }
    const countries = t7;
    const isIdentity = di === IDENTITY_ADDRESS_ANCHOR_DI;
    const t8 = config.allowPoBox;
    const t9 = isIdentity ? "id" : name;
    const t10 = isIdentity ? VAULT_DOT_SEPARATOR : undefined;
    let t11;
    if ($[71] !== t) {
      t11 = t(_temp5);
      $[71] = t;
      $[72] = t11;
    } else {
      t11 = $[72];
    }
    let t12;
    if ($[73] !== t) {
      t12 = t(_temp6);
      $[73] = t;
      $[74] = t12;
    } else {
      t12 = $[74];
    }
    let t13;
    if ($[75] !== t) {
      t13 = t(_temp7);
      $[75] = t;
      $[76] = t13;
    } else {
      t13 = $[76];
    }
    let t14;
    if ($[77] !== t) {
      t14 = t(_temp8);
      $[77] = t;
      $[78] = t14;
    } else {
      t14 = $[78];
    }
    let t15;
    if ($[79] !== t) {
      t15 = t(_temp9);
      $[79] = t;
      $[80] = t15;
    } else {
      t15 = $[80];
    }
    let t16;
    if ($[81] !== t) {
      t16 = t(_temp10);
      $[81] = t;
      $[82] = t16;
    } else {
      t16 = $[82];
    }
    let t17;
    if ($[83] !== t) {
      t17 = t(_temp11);
      $[83] = t;
      $[84] = t17;
    } else {
      t17 = $[84];
    }
    let t18;
    if ($[85] !== t) {
      t18 = t(_temp12);
      $[85] = t;
      $[86] = t18;
    } else {
      t18 = $[86];
    }
    let t19;
    if ($[87] !== t) {
      t19 = t(_temp13);
      $[87] = t;
      $[88] = t19;
    } else {
      t19 = $[88];
    }
    let t20;
    if ($[89] !== t11 || $[90] !== t12 || $[91] !== t13 || $[92] !== t14 || $[93] !== t15 || $[94] !== t16 || $[95] !== t17 || $[96] !== t18 || $[97] !== t19) {
      t20 = {
        country: t11,
        addressLine1: t12,
        addressLine1International: t13,
        addressLine2: t14,
        addressLine2International: t15,
        city: t16,
        state: t17,
        stateInternational: t18,
        zip: t19
      };
      $[89] = t11;
      $[90] = t12;
      $[91] = t13;
      $[92] = t14;
      $[93] = t15;
      $[94] = t16;
      $[95] = t17;
      $[96] = t18;
      $[97] = t19;
      $[98] = t20;
    } else {
      t20 = $[98];
    }
    let t21;
    if ($[99] !== t) {
      t21 = t(_temp14);
      $[99] = t;
      $[100] = t21;
    } else {
      t21 = $[100];
    }
    let t22;
    if ($[101] !== t) {
      t22 = t(_temp15);
      $[101] = t;
      $[102] = t22;
    } else {
      t22 = $[102];
    }
    let t23;
    if ($[103] !== t) {
      t23 = t(_temp16);
      $[103] = t;
      $[104] = t23;
    } else {
      t23 = $[104];
    }
    let t24;
    if ($[105] !== t) {
      t24 = t(_temp17);
      $[105] = t;
      $[106] = t24;
    } else {
      t24 = $[106];
    }
    let t25;
    if ($[107] !== t) {
      t25 = t(_temp18);
      $[107] = t;
      $[108] = t25;
    } else {
      t25 = $[108];
    }
    let t26;
    if ($[109] !== t) {
      t26 = t(_temp19);
      $[109] = t;
      $[110] = t26;
    } else {
      t26 = $[110];
    }
    let t27;
    if ($[111] !== t) {
      t27 = t(_temp20);
      $[111] = t;
      $[112] = t27;
    } else {
      t27 = $[112];
    }
    let t28;
    if ($[113] !== t) {
      t28 = t(_temp21);
      $[113] = t;
      $[114] = t28;
    } else {
      t28 = $[114];
    }
    let t29;
    if ($[115] !== t21 || $[116] !== t22 || $[117] !== t23 || $[118] !== t24 || $[119] !== t25 || $[120] !== t26 || $[121] !== t27 || $[122] !== t28) {
      t29 = {
        country: t21,
        addressLine1: t22,
        addressLine2: t23,
        addressLine2International: t24,
        city: t25,
        state: t26,
        stateInternational: t27,
        zip: t28
      };
      $[115] = t21;
      $[116] = t22;
      $[117] = t23;
      $[118] = t24;
      $[119] = t25;
      $[120] = t26;
      $[121] = t27;
      $[122] = t28;
      $[123] = t29;
    } else {
      t29 = $[123];
    }
    let t30;
    if ($[124] !== t) {
      t30 = t(_temp22);
      $[124] = t;
      $[125] = t30;
    } else {
      t30 = $[125];
    }
    let t31;
    if ($[126] !== t) {
      t31 = t(_temp23);
      $[126] = t;
      $[127] = t31;
    } else {
      t31 = $[127];
    }
    let t32;
    if ($[128] !== t) {
      t32 = t(_temp24);
      $[128] = t;
      $[129] = t32;
    } else {
      t32 = $[129];
    }
    let t33;
    if ($[130] !== t) {
      t33 = t(_temp25);
      $[130] = t;
      $[131] = t33;
    } else {
      t33 = $[131];
    }
    let t34;
    if ($[132] !== t) {
      t34 = t(_temp26);
      $[132] = t;
      $[133] = t34;
    } else {
      t34 = $[133];
    }
    let t35;
    if ($[134] !== t30 || $[135] !== t31 || $[136] !== t32 || $[137] !== t33 || $[138] !== t34) {
      t35 = {
        country: t30,
        addressLine1: t31,
        city: t32,
        state: t33,
        zip: t34
      };
      $[134] = t30;
      $[135] = t31;
      $[136] = t32;
      $[137] = t33;
      $[138] = t34;
      $[139] = t35;
    } else {
      t35 = $[139];
    }
    let t36;
    if ($[140] !== config.allowPoBox || $[141] !== countries || $[142] !== l10n || $[143] !== required || $[144] !== t10 || $[145] !== t20 || $[146] !== t29 || $[147] !== t35 || $[148] !== t9) {
      t36 = <Form.AddressLines allowPoBox={t8} prefix={t9} vaultSeparator={t10} required={required} l10n={l10n} countries={countries} stateOptions={US_STATES_AND_ARMED_FORCES} labels={t20} placeholders={t29} errors={t35} />;
      $[140] = config.allowPoBox;
      $[141] = countries;
      $[142] = l10n;
      $[143] = required;
      $[144] = t10;
      $[145] = t20;
      $[146] = t29;
      $[147] = t35;
      $[148] = t9;
      $[149] = t36;
    } else {
      t36 = $[149];
    }
    return t36;
  }
  return null;
};
function _temp($) {
  return $.shortText.required;
}
function _temp2($_0) {
  return $_0.optional;
}
function _temp3(option) {
  return {
    label: option,
    value: option
  };
}
function _temp4(option_0) {
  return {
    label: option_0,
    value: option_0
  };
}
function _temp5($_1) {
  return $_1.address.country.label;
}
function _temp6($_2) {
  return $_2.address.addressLine1.label;
}
function _temp7($_3) {
  return $_3.address.addressLine1.internationalLabel;
}
function _temp8($_4) {
  return $_4.address.addressLine2.label;
}
function _temp9($_5) {
  return $_5.address.addressLine2.internationalLabel;
}
function _temp10($_6) {
  return $_6.address.city.label;
}
function _temp11($_7) {
  return $_7.address.state.label;
}
function _temp12($_8) {
  return $_8.address.state.internationalLabel;
}
function _temp13($_9) {
  return $_9.address.zip.label;
}
function _temp14($_10) {
  return $_10.address.country.placeholder;
}
function _temp15($_11) {
  return $_11.address.addressLine1.placeholder;
}
function _temp16($_12) {
  return $_12.address.addressLine2.placeholder;
}
function _temp17($_13) {
  return $_13.address.addressLine2.internationalPlaceholder;
}
function _temp18($_14) {
  return $_14.address.city.placeholder;
}
function _temp19($_15) {
  return $_15.address.state.placeholder;
}
function _temp20($_16) {
  return $_16.address.state.internationalPlaceholder;
}
function _temp21($_17) {
  return $_17.address.zip.placeholder;
}
function _temp22($_18) {
  return $_18.address.country.placeholder;
}
function _temp23($_19) {
  return $_19.address.addressLine1.error;
}
function _temp24($_20) {
  return $_20.address.city.error;
}
function _temp25($_21) {
  return $_21.address.state.error;
}
function _temp26($_22) {
  return $_22.address.zip.error;
}